import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Package, ShoppingBag, Minus, Plus, Lock } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { money } from "../../lib/helpers";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Field, NativeSelect } from "../../components/Common";
import PublicFrame, { usePublicBranding } from "./PublicFrame";
import { LoginBox, L } from "../portal/Portal";
import { BookingPay } from "../../components/BookingPay";

function RequestAccess({ slug }) {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", marketing_consent: false });
  const [done, setDone] = useState(false);
  const send = async () => { try { await api.post(`/shop/${slug}/register`, f); setDone(true); } catch (e) { toast.error(errMsg(e)); } };
  if (done) return <div className="rounded-[var(--radius)] border bg-card p-6 text-sm" data-testid="shop-request-sent">Thank you! We'll email you as soon as your access is approved.</div>;
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="shop-request-form">
      <h2 className="text-lg font-semibold">Request access</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {["first_name", "last_name", "email", "phone"].map((k) => <Input key={k} data-testid={`shop-req-${k}`} placeholder={k.replace("_", " ")} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />)}
      </div>
      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="shop-req-marketing" checked={f.marketing_consent} onCheckedChange={(v) => setF({ ...f, marketing_consent: !!v })} />Send me news and offers</label>
      <Button className="rounded-full" data-testid="shop-req-submit" disabled={!f.first_name || !f.last_name || !f.email} onClick={send}>Request access</Button>
    </div>
  );
}

function Checkout({ slug, info, cart, products, currency, embed, onQty, onPaid }) {
  const items = Object.entries(cart).map(([id, qty]) => ({ p: products.find((x) => x.id === id), qty })).filter((x) => x.p);
  const canPickup = info.pickup_locations.length > 0 && items.every((i) => i.p.pickup_allowed);
  const canDeliver = items.every((i) => i.p.delivery_allowed);
  const [mode, setMode] = useState(canDeliver ? "delivery" : "pickup");
  const [pickup, setPickup] = useState(info.pickup_locations[0]?.id || "");
  const [addr, setAddr] = useState({ line1: "", city: "", postcode: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState({ code: "", gift_code: "", pay_full: true });
  const [quote, setQuote] = useState(null);
  if (!items.length) return null;
  const lines = items.map((i) => ({ product_id: i.p.id, qty: i.qty }));
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shop/${slug}/checkout`, { items: lines, currency, fulfillment: mode, code: codes.code, gift_code: codes.gift_code,
        pickup_location_id: mode === "pickup" ? pickup : null, address: mode === "delivery" ? addr : {}, origin_url: window.location.origin });
      if (data.paid) { toast.success("Order placed and paid with your gift card"); setBusy(false); onPaid(); return; }
      sessionStorage.setItem("mcm_kind", "order"); sessionStorage.setItem("mcm_slug", slug);
      if (embed) { window.open(data.checkout_url, "_blank"); setBusy(false); } else window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 space-y-4" data-testid="shop-cart">
      <h2 className="text-lg font-semibold flex items-center gap-2"><ShoppingBag className="h-5 w-5" />Your basket</h2>
      {items.map(({ p, qty }) => (
        <div key={p.id} className="flex items-center gap-3 text-sm">
          <span className="flex-1">{p.name}</span>
          <button aria-label="Decrease" data-testid={`cart-minus-${p.id}`} onClick={() => onQty(p.id, qty - 1)}><Minus className="h-4 w-4" /></button>
          <span className="w-6 text-center">{qty}</span>
          <button aria-label="Increase" data-testid={`cart-plus-${p.id}`} onClick={() => onQty(p.id, qty + 1)}><Plus className="h-4 w-4" /></button>
          <span className="w-24 text-right">{money(p.prices[currency] * qty, currency)}</span>
        </div>
      ))}
      <div className="flex gap-2">
        {canDeliver && <button data-testid="cart-mode-delivery" onClick={() => setMode("delivery")} className={`rounded-full border px-4 py-1.5 text-sm ${mode === "delivery" ? "bg-primary text-primary-foreground" : ""}`}>Delivery</button>}
        {canPickup && <button data-testid="cart-mode-pickup" onClick={() => setMode("pickup")} className={`rounded-full border px-4 py-1.5 text-sm ${mode === "pickup" ? "bg-primary text-primary-foreground" : ""}`}>Collect</button>}
      </div>
      {!canDeliver && !canPickup && <p className="text-sm text-destructive">These items can't be ordered together. Please split your order.</p>}
      {mode === "pickup" ? (
        <NativeSelect testId="cart-pickup-select" value={pickup} onChange={setPickup} options={info.pickup_locations.map((l) => ({ value: l.id, label: `${l.name} ${l.address || ""}` }))} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {[["line1", "Address"], ["city", "City"], ["postcode", "Postcode"], ["country", "Country"]].map(([k, label]) => (
            <Field key={k} label={label} className={k === "line1" ? "sm:col-span-2" : ""}><Input data-testid={`cart-addr-${k}`} value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} /></Field>
          ))}
        </div>
      )}
      <BookingPay slug={slug} quoteUrl={`/shop/${slug}/quote`} base={{ items: lines, currency, fulfillment: mode }} value={codes} onChange={setCodes} onQuote={setQuote} allowDeposit={false} totalLabel="Order total" />
      <Button className="w-full rounded-full h-11" data-testid="cart-pay-btn" disabled={busy || !quote || (!canDeliver && !canPickup)} onClick={pay}>
        {quote && quote.charge_now <= 0 ? "Place order" : `Pay securely${quote ? ` · ${money(quote.charge_now, currency)}` : ""}`}</Button>
    </div>
  );
}

export default function ShopFront() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const { lang } = useT();
  const [info, setInfo] = useState(null);
  const [me, setMe] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [currency, setCurrency] = useState("");
  const [cart, setCart] = useState({});
  usePublicBranding(info);

  const loadMe = useCallback(() => api.get(`/shop/${slug}/me`).then((r) => setMe(r.data)).catch(() => setMe(null)), [slug]);
  const loadOrders = () => api.get(`/shop/${slug}/orders`).then((r) => setOrders(r.data));
  useEffect(() => {
    api.get(`/shop/${slug}/info`).then((r) => { setInfo(r.data); setCurrency(r.data.currencies[0] || "EUR"); }).catch(() => setInfo(false));
    loadMe();
  }, [slug, loadMe]);
  useEffect(() => {
    if (me?.shop_status === "approved") {
      api.get(`/shop/${slug}/products`).then((r) => setProducts(r.data));
      loadOrders();
    }
  }, [me, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (info === false) return <PublicFrame embed={embed}><p data-testid="shop-not-found">Shop not available.</p></PublicFrame>;
  if (!info || me === undefined) return <PublicFrame embed={embed}><p>…</p></PublicFrame>;
  const setQty = (id, q) => setCart((c) => { const n = { ...c }; if (q <= 0) delete n[id]; else n[id] = q; return n; });
  const visible = products.filter((p) => p.prices[currency] != null);

  return (
    <PublicFrame branding={info.branding} embed={embed}>
      <h1 className="text-3xl sm:text-4xl font-bold mb-2">Shop</h1>
      {info.intro && <p className="text-muted-foreground mb-6 whitespace-pre-wrap">{info.intro}</p>}
      {!me && (
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <RequestAccess slug={slug} />
          <div className="space-y-3"><h2 className="text-lg font-semibold flex items-center gap-2"><Lock className="h-4 w-4" />Already approved? Log in</h2>
            <LoginBox slug={slug} l={L[lang] || L.en} onIn={loadMe} /></div>
        </div>
      )}
      {me && me.shop_status !== "approved" && (
        <div className="rounded-[var(--radius)] border bg-card p-6 mt-6 space-y-3" data-testid="shop-pending">
          <p>Hi {me.first_name}, {me.shop_status === "pending" ? "your access request is waiting for approval. We'll email you once approved." : me.shop_status === "rejected" ? "the shop is not available for your account." : "request access to see our products."}</p>
          {me.shop_status === "none" && <Button className="rounded-full" data-testid="shop-request-access-btn" onClick={() => api.post(`/shop/${slug}/request-access`).then(loadMe)}>Request access</Button>}
        </div>
      )}
      {me?.shop_status === "approved" && (
        <div className="space-y-8 mt-4">
          {info.currencies.length > 1 && <NativeSelect testId="shop-currency-select" value={currency} onChange={(v) => { setCurrency(v); setCart({}); }} options={info.currencies.map((c) => ({ value: c, label: c }))} />}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="shop-products">
            {!visible.length && <p className="text-sm text-muted-foreground col-span-full">No products available yet.</p>}
            {visible.map((p) => (
              <div key={p.id} className="lift rounded-[var(--radius)] border bg-card overflow-hidden flex flex-col" data-testid={`shop-product-${p.id}`}>
                <div className="aspect-square bg-muted grid place-items-center">{p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <Package className="h-8 w-8 text-muted-foreground" />}</div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <div className="font-semibold text-sm">{p.name}</div>
                  {p.description && <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="font-semibold">{money(p.prices[currency], currency, lang)}</span>
                    <Button size="sm" className="rounded-full" data-testid={`add-to-cart-${p.id}`} disabled={p.stock === 0} onClick={() => setQty(p.id, (cart[p.id] || 0) + 1)}>{p.stock === 0 ? "Sold out" : "Add"}</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Checkout slug={slug} info={info} cart={cart} products={products} currency={currency} embed={embed} onQty={setQty} onPaid={() => { setCart({}); loadOrders(); }} />
          {orders.length > 0 && (
            <div className="space-y-2" data-testid="shop-my-orders"><h2 className="text-lg font-semibold">Your orders</h2>
              {orders.map((o) => <div key={o.id} className="rounded-md border bg-card p-3 text-sm flex justify-between"><span>{o.number} · {o.created_at.slice(0, 10)} · {o.status.replace(/_/g, " ")}</span><span>{money(o.total, o.currency, lang)}</span></div>)}
            </div>
          )}
        </div>
      )}
    </PublicFrame>
  );
}
